import React, { useEffect, useRef, useState } from 'react';
import { Gauge, X } from 'lucide-react';
import { getMasterAnalyser, getTrackAnalyser } from '@/audio/AudioEngine';
import { EffectsPopover } from './EffectsPopover';
import { MixRange, PanKnob } from './MixControls';

const amountText = (track, effect) => effect === 'eq'
  ? (Object.values(track.effects?.eq || {}).some(value => Number(value) !== 0) || Number(track.effects?.highPassHz) > 0 || Number(track.effects?.lowPassHz) > 0 ? 'Custom' : 'Flat')
  : `${Math.round(Number(track.effects?.[effect] || 0) * 100)}%`;

export const MIXER_FADER_HEIGHT = 96;

export function LevelMeter({ isPlaying, analyser: analyserGetter, testId }) {
  const [level, setLevel] = useState(0);
  const peakRef = useRef(0);
  useEffect(() => {
    if (!isPlaying) { setLevel(0); peakRef.current = 0; return undefined; }
    const analyser = analyserGetter?.();
    if (!analyser) return undefined;
    const size = analyser.fftSize || 256;
    const floatData = typeof analyser.getFloatTimeDomainData === 'function' ? new Float32Array(size) : null;
    const byteData = floatData ? null : new Uint8Array(size);
    let frame = null; let last = -Infinity;
    const tick = (time = 0) => {
      if (time - last >= 32) {
        last = time;
        if (floatData) analyser.getFloatTimeDomainData(floatData); else analyser.getByteTimeDomainData(byteData);
        const data = floatData || byteData;
        let sum = 0; let peak = 0;
        for (let index = 0; index < data.length; index += 1) {
          const sample = floatData ? data[index] : (data[index] - 128) / 128;
          sum += sample * sample; peak = Math.max(peak, Math.abs(sample));
        }
        const rms = Math.sqrt(sum / data.length);
        peakRef.current = Math.max(peak, peakRef.current * 0.9);
        setLevel(Math.min(1, Math.max(rms * 1.8, peakRef.current)));
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => { if (frame != null) cancelAnimationFrame(frame); };
  }, [isPlaying, analyserGetter]);
  return <div data-testid={testId} className="ba-level-meter" aria-label="Audio level"><i style={{ height: `${Math.round(level * 100)}%` }} /></div>;
}

function ChannelStrip({ track, isPlaying, producerMode, onMute, onSolo, onMixDraft, onMixCommit, analyserForTrack }) {
  const [effect, setEffect] = useState(null);
  return <section className="ba-channel-strip" data-testid={`mixer-strip-${track.id}`} data-instrument={track.instrument}>
    <header title={track.name}><span>{track.name}</span></header>
    <div className="ba-mixer-fx-slots">
      {['eq', 'reverb', 'echo', 'punch'].map((id) => <button key={id} data-testid={`mixer-fx-${id}-${track.id}`} onClick={() => setEffect(id)}>
        <strong>{id === 'eq' ? 'EQ' : id[0].toUpperCase() + id.slice(1)}</strong><span>{amountText(track, id)}</span>
      </button>)}
      {effect && <div className="ba-mixer-fx-popover"><EffectsPopover key={effect} track={track} producerMode={producerMode} initialEffect={effect} onDraft={onMixDraft} onCommit={onMixCommit} onClose={() => setEffect(null)} /></div>}
    </div>
    <PanKnob trackId={`mixer-${track.id}`} value={track.pan} onDraft={(pan) => onMixDraft?.(track.id, { pan })} onCommit={(pan) => onMixCommit?.(track.id, { pan })} />
    <div className="ba-fader-stack" data-fader-height={MIXER_FADER_HEIGHT}>
      <LevelMeter testId={`meter-${track.id}`} isPlaying={isPlaying} analyser={() => analyserForTrack(track.id)} />
      <MixRange testId={`mixer-volume-${track.id}`} label={`${track.name} volume`} vertical min={0} max={100} value={track.volume}
        onDraft={(volume) => onMixDraft?.(track.id, { volume })} onCommit={(volume) => onMixCommit?.(track.id, { volume })} />
    </div>
    <output>{Math.round(track.volume)}</output>
    <div className="ba-mixer-buttons"><button data-testid={`mixer-mute-${track.id}`} className={track.muted ? 'active mute' : ''} onClick={() => onMute?.(track.id)}>M</button><button data-testid={`mixer-solo-${track.id}`} className={track.solo ? 'active solo' : ''} onClick={() => onSolo?.(track.id)}>S</button></div>
  </section>;
}

export function MixerPanel({ tracks, isPlaying = false, producerMode = false, masterVolume = 100, onMasterDraft, onMasterCommit, onMute, onSolo, onMixDraft, onMixCommit, onClose, analyserForTrack = getTrackAnalyser, masterAnalyser = getMasterAnalyser }) {
  return <div className="ba-bottom-panel ba-mixer-panel" data-testid="mixer-panel">
    <div className="ba-bottom-panel-title"><Gauge size={16} /><strong>Mixer</strong><span>{tracks.length} tracks</span><button aria-label="Close mixer" onClick={onClose}><X size={16} /></button></div>
    <div className="ba-mixer-scroll">
      {tracks.map((track) => <ChannelStrip key={track.id} track={track} isPlaying={isPlaying} producerMode={producerMode} onMute={onMute} onSolo={onSolo} onMixDraft={onMixDraft} onMixCommit={onMixCommit} analyserForTrack={analyserForTrack} />)}
      <section className="ba-channel-strip ba-master-strip" data-testid="mixer-master-strip">
        <header><span>Master</span></header>
        <div className="ba-limiter-indicator"><i /> Limiter on</div>
        <div className="ba-fader-stack" data-fader-height={MIXER_FADER_HEIGHT}>
          <LevelMeter testId="meter-master" isPlaying={isPlaying} analyser={masterAnalyser} />
          <MixRange testId="master-volume" label="Master volume" vertical min={0} max={100} value={masterVolume} onDraft={onMasterDraft} onCommit={onMasterCommit} />
        </div>
        <output>{Math.round(masterVolume)}</output>
      </section>
    </div>
  </div>;
}

export default MixerPanel;
